/**
 * Primitivele de interfata. Toate culorile trec prin variabilele temei, deci
 * niciuna nu presupune ca fundalul e alb.
 *
 * Accesibilitate, tratata aici o singura data ca sa nu depinda de disciplina
 * fiecarei pagini: fiecare camp are eticheta legata prin `id`, mesajul de
 * eroare e legat prin `aria-describedby` si marcat `aria-invalid`, iar starea de
 * incarcare a butonului il si dezactiveaza (altfel dublu-click = doua comenzi).
 *
 * Marimile butoanelor si controalele mici (chip, stepper, buton-pictograma)
 * stau tot aici: sunt aceleasi pe card, in fisa produsului, in cos si la
 * finalizare, iar diferentele de un pixel intre trei copii ale aceluiasi
 * stepper se citesc ca dezordine.
 */

import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

import { formatRon, type MoneyMinor } from "@deliveryapp/shared";

import { IconMinus, IconPlus } from "./icons";

// ---------------------------------------------------------------------------
// Buton
// ---------------------------------------------------------------------------

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "soft" | "contrast";
type ButtonSize = "sm" | "md" | "lg";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-brand text-brand-contrast border border-transparent hover:bg-brand-hover shadow-sm",
  secondary: "bg-surface text-ink border border-line hover:bg-surface-hover",
  ghost: "bg-transparent text-ink border border-transparent hover:bg-surface",
  danger: "bg-danger-soft text-danger border border-transparent hover:opacity-90",
  soft: "bg-brand-soft text-brand-on-surface border border-transparent hover:opacity-90",
  contrast: "bg-white text-black border border-transparent hover:opacity-90",
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: "min-h-9 px-3 py-1.5 text-sm",
  md: "min-h-11 px-4 py-2.5 text-base",
  lg: "min-h-13 px-5 py-3 text-base",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  loading?: boolean;
  /** Pictograma dinaintea textului. */
  icon?: ReactNode;
  /** Pictograma sau suma de dupa text, impinsa la dreapta cand butonul e `block`. */
  trailing?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    block = false,
    loading = false,
    disabled,
    icon,
    trailing,
    children,
    className = "",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? "button"}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={[
        "da-press inline-flex items-center justify-center gap-2 rounded-control font-semibold",
        "transition-[background-color,opacity,transform] disabled:cursor-not-allowed disabled:opacity-50",
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        block ? "w-full" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {loading ? <Spinner size={18} /> : icon}
      {children}
      {trailing === undefined ? null : <span className={block ? "ml-auto" : ""}>{trailing}</span>}
    </button>
  );
});

/**
 * Buton rotund cu o singura pictograma. `aria-label` e OBLIGATORIU: pictograma
 * e decor, iar fara eticheta cititorul de ecran anunta „buton".
 */
export function IconButton({
  label,
  children,
  className = "",
  variant = "secondary",
  size = 44,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label" | "children"> & {
  label: string;
  children: ReactNode;
  variant?: "secondary" | "ghost" | "primary" | "glass";
  size?: number;
}) {
  const look =
    variant === "primary"
      ? "bg-brand text-brand-contrast hover:bg-brand-hover shadow-sm"
      : variant === "glass"
        ? "bg-canvas/90 text-ink shadow-md backdrop-blur hover:bg-canvas"
        : variant === "ghost"
          ? "bg-transparent text-ink hover:bg-surface"
          : "bg-surface text-ink border border-line hover:bg-surface-hover";
  return (
    <button
      type="button"
      aria-label={label}
      className={`da-press inline-grid shrink-0 place-items-center rounded-full transition-colors disabled:opacity-50 ${look} ${className}`}
      style={{ width: size, height: size, minHeight: size }}
      {...rest}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Chip (filtru, categorie)
// ---------------------------------------------------------------------------

export function Chip({
  selected = false,
  children,
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={[
        "da-press inline-flex min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors",
        selected
          ? "border-brand bg-brand text-brand-contrast"
          : "border-line bg-surface text-ink hover:border-brand hover:text-brand-on-surface",
        className,
      ].join(" ")}
      {...rest}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Stepper de cantitate
// ---------------------------------------------------------------------------

/**
 * `−  n  +`, acelasi peste tot. Butoanele sunt REALE (Baymard: 97% dintre
 * site-uri nu folosesc butoane pentru cantitate, iar campul numeric pe telefon
 * deschide tastatura peste lista). `name` intra in etichetele accesibile, ca
 * „Creste cantitatea" sa spuna pentru ce produs.
 */
export function Stepper({
  value,
  onDecrease,
  onIncrease,
  name,
  min = 0,
  max,
  size = "md",
  decreaseLabel,
  className = "",
}: {
  value: number;
  onDecrease: () => void;
  onIncrease: () => void;
  name: string;
  min?: number;
  max?: number | null;
  size?: "sm" | "md";
  /** Cand scaderea scoate produsul din cos, eticheta o spune. */
  decreaseLabel?: string;
  className?: string;
}) {
  const dimension = size === "sm" ? "h-9 w-9" : "h-11 w-11";
  const canDecrease = value > min;
  const canIncrease = max === null || max === undefined || value < max;
  return (
    <div
      className={`inline-flex items-center rounded-full border border-line bg-canvas p-0.5 ${className}`}
      role="group"
      aria-label={`Cantitate pentru ${name}`}
    >
      <button
        type="button"
        onClick={onDecrease}
        disabled={!canDecrease}
        aria-label={decreaseLabel ?? `Scade cantitatea pentru ${name}`}
        className={`da-press grid ${dimension} place-items-center rounded-full text-ink hover:bg-surface-hover disabled:opacity-40`}
      >
        <IconMinus size={18} />
      </button>
      <span aria-live="polite" className="min-w-7 text-center text-base font-bold tabular-nums text-ink">
        {value}
      </span>
      <button
        type="button"
        onClick={onIncrease}
        disabled={!canIncrease}
        aria-label={`Crește cantitatea pentru ${name}`}
        className={`da-press grid ${dimension} place-items-center rounded-full text-ink hover:bg-surface-hover disabled:opacity-40`}
      >
        <IconPlus size={18} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campuri de formular
// ---------------------------------------------------------------------------

interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  /** Ascunde „(opțional)" — pentru campurile unde e evident. */
  quiet?: boolean;
  children: (ids: { inputId: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

function FieldShell({ label, hint, error, required = false, quiet = false, children }: FieldShellProps) {
  const base = useId();
  const inputId = `${base}-input`;
  const hintId = hint === undefined ? undefined : `${base}-hint`;
  const errorId = error ? `${base}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-semibold text-ink">
        {label}
        {required || quiet ? null : <span className="font-normal text-ink-muted"> (opțional)</span>}
      </label>
      {children({ inputId, describedBy, invalid: Boolean(error) })}
      {hint === undefined || error ? null : (
        <p id={hintId} className="text-xs text-ink-muted">
          {hint}
        </p>
      )}
      {error ? (
        <p id={errorId} role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const CONTROL_CLASS =
  "w-full rounded-control border bg-canvas px-3.5 py-3 text-base text-ink placeholder:text-ink-muted " +
  "transition-colors focus:border-brand disabled:opacity-60";

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "className"> {
  label: string;
  hint?: string;
  error?: string | null;
  quiet?: boolean;
  /** Pictograma sau text mic in stanga campului. */
  leading?: ReactNode;
}

export function Field({ label, hint, error, required, quiet, leading, ...rest }: FieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required === true} quiet={quiet === true}>
      {({ inputId, describedBy, invalid }) => (
        <div className="relative">
          {leading === undefined ? null : (
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-muted">{leading}</span>
          )}
          <input
            id={inputId}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            required={required}
            className={`${CONTROL_CLASS} ${invalid ? "border-danger" : "border-line"} ${leading === undefined ? "" : "pl-10"}`}
            {...rest}
          />
        </div>
      )}
    </FieldShell>
  );
}

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id" | "className"> {
  label: string;
  hint?: string;
  error?: string | null;
  quiet?: boolean;
}

export function TextArea({ label, hint, error, required, quiet, ...rest }: TextAreaProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required === true} quiet={quiet === true}>
      {({ inputId, describedBy, invalid }) => (
        <textarea
          id={inputId}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          rows={rest.rows ?? 3}
          className={`${CONTROL_CLASS} ${invalid ? "border-danger" : "border-line"}`}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

/** Un rand de alegere (radio sau bifa) cu aspect de card, ca in fisa produsului. */
export function ChoiceRow({
  checked,
  onChange,
  type = "radio",
  name,
  value,
  disabled = false,
  title,
  description,
  trailing,
}: {
  checked: boolean;
  onChange: () => void;
  type?: "radio" | "checkbox";
  name: string;
  value: string;
  disabled?: boolean;
  title: ReactNode;
  description?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <label
      className={[
        "flex min-h-14 items-center gap-3 rounded-control border px-3 py-2 text-sm transition-colors",
        disabled ? "cursor-not-allowed border-line bg-canvas text-ink-muted opacity-60" : "cursor-pointer",
        checked ? "border-brand bg-brand-soft text-ink" : "border-line bg-canvas text-ink hover:border-brand",
      ].join(" ")}
    >
      <input type={type} name={name} value={value} disabled={disabled} checked={checked} onChange={onChange} className="sr-only" />
      <span
        className={[
          "grid h-5 w-5 shrink-0 place-items-center border-2",
          type === "radio" ? "rounded-full" : "rounded-md",
          checked ? "border-brand bg-brand text-brand-contrast" : "border-line bg-canvas",
        ].join(" ")}
        aria-hidden="true"
      >
        {checked ? (type === "radio" ? <span className="h-2 w-2 rounded-full bg-current" /> : "✓") : null}
      </span>
      <span className="flex flex-1 flex-col gap-0.5 font-semibold">
        <span>{title}</span>
        {description === undefined ? null : <span className="text-xs font-normal text-ink-muted">{description}</span>}
      </span>
      {trailing}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Mesaje
// ---------------------------------------------------------------------------

export type AlertTone = "error" | "warning" | "info" | "success";

const ALERT_TONES: Record<AlertTone, { border: string; text: string; label: string }> = {
  error: { border: "border-danger", text: "text-danger", label: "Eroare" },
  warning: { border: "border-warning", text: "text-warning", label: "Atenție" },
  info: { border: "border-line", text: "text-ink", label: "Informație" },
  success: { border: "border-success", text: "text-success", label: "Confirmare" },
};

export interface AlertProps {
  tone: AlertTone;
  title?: string;
  children?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
}

export function Alert({ tone, title, children, actions, icon }: AlertProps) {
  const style = ALERT_TONES[tone];
  return (
    <div role={tone === "error" ? "alert" : "status"} className={`flex gap-3 rounded-card border bg-surface p-4 ${style.border}`}>
      {icon === undefined ? null : <span className={`mt-0.5 shrink-0 ${style.text}`}>{icon}</span>}
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${style.text}`}>{title ?? style.label}</p>
        {children === undefined ? null : <div className="mt-1 text-sm text-ink">{children}</div>}
        {actions === undefined ? null : <div className="mt-3 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function Spinner({ size = 20 }: { size?: number }) {
  return (
    <span
      role="presentation"
      style={{ width: size, height: size, borderWidth: Math.max(2, Math.round(size / 10)) }}
      className="inline-block animate-spin rounded-full border-current border-t-transparent align-[-2px]"
    />
  );
}

// ---------------------------------------------------------------------------
// Afisari mici
// ---------------------------------------------------------------------------

export function Price({ amount, className = "" }: { amount: MoneyMinor; className?: string }) {
  // Formatarea vine din `shared`, aceeasi functie pe care o foloseste core-ul la
  // construirea bonului: totalul afisat aici si cel de pe bon nu au de unde sa
  // difere prin rotunjire.
  return <span className={`tabular-nums ${className}`}>{formatRon(amount)}</span>;
}

export type BadgeTone = "neutral" | "success" | "warning" | "danger" | "brand" | "promo";

export function Badge({ children, tone = "neutral", className = "" }: { children: ReactNode; tone?: BadgeTone; className?: string }) {
  const toneClass =
    tone === "success"
      ? "text-success border-success bg-surface"
      : tone === "warning"
        ? "text-warning border-warning bg-surface"
        : tone === "danger"
          ? "text-danger border-danger bg-surface"
          : tone === "brand"
            ? "text-brand-on-surface border-transparent bg-brand-soft"
            : tone === "promo"
              ? "text-white border-transparent bg-danger"
              : "text-ink-muted border-line bg-surface";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-bold ${toneClass} ${className}`}>
      {children}
    </span>
  );
}

/** Un rand de informatie cu pictograma: „⏱ 35–50 min", „🛵 12 lei". */
export function Meta({ icon, children, className = "" }: { icon?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-sm text-ink-muted ${className}`}>
      {icon === undefined ? null : <span className="shrink-0 opacity-80">{icon}</span>}
      <span className="min-w-0">{children}</span>
    </span>
  );
}

/**
 * `level` exista pentru ca fiecare pagina sa aiba exact un `h1`. Cititoarele de
 * ecran folosesc ierarhia de titluri ca sa navigheze; o pagina care incepe cu
 * `h2` nu are punct de intrare.
 */
export function SectionHeading({
  children,
  id,
  level = 2,
  className = "",
}: {
  children: ReactNode;
  id?: string;
  level?: 1 | 2;
  className?: string;
}) {
  if (level === 1) {
    return (
      <h1 id={id} className={`text-2xl font-extrabold tracking-tight text-ink sm:text-3xl ${className}`}>
        {children}
      </h1>
    );
  }
  return (
    <h2 id={id} className={`text-lg font-bold text-ink sm:text-xl ${className}`}>
      {children}
    </h2>
  );
}

/** Bara de progres subtire (comanda minima, livrare gratuita). */
export function ProgressBar({ percent, label }: { percent: number; label: string }) {
  const clamped = Math.min(100, Math.max(0, Math.round(percent)));
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-surface-raised"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      aria-label={label}
    >
      <div className="h-full rounded-full bg-brand transition-[width] duration-300" style={{ width: `${clamped}%` }} />
    </div>
  );
}
